/**
 * SalonTools3D — GLB-based 3D salon tools (scissors, comb, mirror)
 *
 * High-fidelity Meshy AI generated assets for Origin that float
 * and respond to mouse movement and scroll.
 */

import { useRef, useState, useEffect, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Float, Environment, useGLTF } from '@react-three/drei'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import * as THREE from 'three'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import gsap from 'gsap'

gsap.registerPlugin(ScrollTrigger)

// Asset paths
const SCISSOR_GLB = '/assets/3d/Meshy_AI_Blue_and_Gold_Scissor_0328152850_texture.glb'
const COMB_GLB = '/assets/3d/Meshy_AI_Golden_Comb_with_Blue_0328152742_texture.glb'
const MIRROR_GLB = '/assets/3d/Meshy_AI_Golden_Frame_Blue_Han_0328152009_texture.glb'

// Preload models
useGLTF.preload(SCISSOR_GLB)
useGLTF.preload(COMB_GLB)
useGLTF.preload(MIRROR_GLB)

interface ToolProps {
  position?: [number, number, number]
  rotation?: [number, number, number]
  scale?: number
}

/** Scissors from GLB */
function Scissors({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }: ToolProps) {
  const { scene } = useGLTF(SCISSOR_GLB)
  const ref = useRef<THREE.Group>(null)

  useFrame((state) => {
    if (ref.current) {
      // Gentle wobble
      ref.current.rotation.z = rotation[2] + Math.sin(state.clock.elapsedTime * 0.8) * 0.1
    }
  })

  return (
    <primitive
      ref={ref}
      object={scene.clone()}
      position={position}
      rotation={rotation}
      scale={scale}
    />
  )
}

/** Comb from GLB */
function Comb({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }: ToolProps) {
  const { scene } = useGLTF(COMB_GLB)

  return <primitive object={scene.clone()} position={position} rotation={rotation} scale={scale} />
}

/** Mirror/Frame from GLB */
function Mirror({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }: ToolProps) {
  const { scene } = useGLTF(MIRROR_GLB)
  const ref = useRef<THREE.Group>(null)

  useFrame((state) => {
    if (ref.current) {
      // Subtle tilt toward camera
      ref.current.rotation.y = rotation[1] + Math.sin(state.clock.elapsedTime * 0.5) * 0.15
    }
  })

  return (
    <primitive
      ref={ref}
      object={scene.clone()}
      position={position}
      rotation={rotation}
      scale={scale}
    />
  )
}

/** Main scene with all tools */
function ToolsScene({ progress }: { progress: number }) {
  const groupRef = useRef<THREE.Group>(null)

  useFrame((state) => {
    if (groupRef.current) {
      // Mouse parallax
      groupRef.current.rotation.y = state.pointer.x * 0.3
      groupRef.current.rotation.x = state.pointer.y * 0.15
      // Scroll-based rotation
      groupRef.current.rotation.z = progress * Math.PI * 0.25
    }
  })

  return (
    <group ref={groupRef}>
      <Suspense fallback={null}>
        <Float speed={2} rotationIntensity={0.3} floatIntensity={0.5}>
          <Scissors position={[-3, 1.5, 0]} rotation={[0, 0.5, 0.3]} scale={1.8} />
        </Float>
        <Float speed={1.5} rotationIntensity={0.4} floatIntensity={0.6}>
          <Comb position={[2.5, -1.8, 0.5]} rotation={[0.2, -0.3, 0.1]} scale={1.8} />
        </Float>
        <Float speed={1.8} rotationIntensity={0.2} floatIntensity={0.4}>
          <Mirror position={[0, -0.2, -1]} rotation={[0, 0, -0.2]} scale={1.4} />
        </Float>
      </Suspense>

      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 5, 5]} intensity={1.5} color="#ffffff" />
      <directionalLight position={[-3, -3, 2]} intensity={0.6} color="#005ea1" />
      <pointLight position={[0, 0, 3]} intensity={0.5} color="#C9A84C" />
    </group>
  )
}

interface SalonTools3DProps {
  sectionRef?: React.RefObject<HTMLElement | null>
  scrollStart?: string
  scrollEnd?: string
  className?: string
}

export function SalonTools3D({
  sectionRef,
  scrollStart = 'top center',
  scrollEnd = 'bottom center',
  className = '',
}: SalonTools3DProps) {
  const [progress, setProgress] = useState(0)
  const [mounted, setMounted] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Delay mount to ensure DOM is ready
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!mounted) return
    const trigger = sectionRef?.current || containerRef.current?.parentElement
    if (!trigger) return

    const st = ScrollTrigger.create({
      trigger,
      start: scrollStart,
      end: scrollEnd,
      scrub: 1,
      onUpdate: (self) => setProgress(self.progress),
    })

    return () => st.kill()
  }, [sectionRef, scrollStart, scrollEnd, mounted])

  return (
    <div
      ref={containerRef}
      className={`pointer-events-none ${className}`}
      style={{ width: '100%', height: '100%' }}
      aria-hidden="true"
      data-testid="salon-tools-3d"
    >
      {mounted && (
        <Canvas
          camera={{ position: [0, 0, 6], fov: 45 }}
          dpr={[1, 2]}
          gl={{ alpha: true, antialias: true, powerPreference: 'high-performance' }}
          frameloop="always"
          onCreated={({ gl }) => {
            gl.setClearColor(0x000000, 0)
          }}
        >
          <ToolsScene progress={progress} />
          <Environment preset="studio" />
          <EffectComposer enableNormalPass={false}>
            <Bloom luminanceThreshold={0.4} luminanceSmoothing={0.9} intensity={0.6} />
          </EffectComposer>
        </Canvas>
      )}
    </div>
  )
}

export default SalonTools3D
